import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Section, Button, showAlert, showConfirm } from '@platform/ui';
import { toUserMessage } from '@platform/api-client';
import { SEO } from '@platform/seo';
import { buildSeo } from '@/shared/seo/seoBuilder';
import type { BoardType } from '@/shared/constants/app/routes.constant.ts';
import { useBoardDetail } from '../api/use-board.query.ts';
import { useDeletePost } from '../api/use-board.mutation.ts';
import { useBoardPermissions } from '../hooks/useBoardPermissions.ts';
import { PostViewer } from '../components/PostViewer.tsx';
import { AttachmentList } from '../components/AttachmentList.tsx';
import { DetailNav } from '../components/DetailNav.tsx';
import { BoardStateView } from '../components/BoardStateView.tsx';
import {
  formatBoardDateTime,
  formatBoardDate,
} from '../common/utils.ts';
import type { BoardConfig, Post } from '../types/board.type.ts';

interface BoardDetailViewProps {
  type: BoardType;
  config: BoardConfig<Post>;
}

/**
 * 게시글 상세 컨테이너.
 * URL :id 로 상세 조회 → 본문(Viewer)/첨부/메타 렌더.
 * 권한 있으면 수정/삭제 노출. 삭제는 showConfirm(danger)로 확인 후 mutation.
 *
 * prev/next: 현재 BE 미연동(mock)이라 detail 응답에 없으면 생략.
 * BE 가 detail 응답에 prev/next 요약을 포함하면 그대로 표시된다.
 */
export function BoardDetailView({ type, config }: BoardDetailViewProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: post, isLoading, isError, refetch } = useBoardDetail(type, id);
  const perms = useBoardPermissions(type);
  const deleteMutation = useDeletePost(type);

  const handleDelete = useCallback(async () => {
    if (!post) return;
    const ok = await showConfirm({
      title: '게시글 삭제',
      message: '이 게시글을 삭제하시겠습니까? 되돌릴 수 없습니다.',
      tone: 'danger',
      confirmText: '삭제',
      cancelText: '취소',
      dismissible: false,
    });
    if (!ok) return;

    // 응답 값을 쓰지 않으므로 mutate + 콜백. 실패 사유는 BE 문구를 그대로 노출한다.
    // (기존엔 showConfirm 을 알림 용도로 써서 불필요한 '취소' 버튼이 노출됐다.)
    deleteMutation.mutate(post.id, {
      onSuccess: () => navigate(config.basePath, { replace: true }),
      onError: (error) =>
        void showAlert({
          title: '삭제 실패',
          message: toUserMessage(error, '삭제 중 오류가 발생했습니다.'),
          tone: 'danger',
          confirmText: '확인',
        }),
    });
  }, [post, deleteMutation, navigate, config.basePath]);

  if (isLoading) {
    return <BoardStateView kind="loading" />;
  }
  if (isError || !post) {
    return (
      <BoardStateView
        kind="error"
        onRetry={() => refetch()}
        backTo={config.basePath}
      />
    );
  }

  const seo = buildSeo({
    title: post.title,
    description: post.excerpt ?? config.description,
    path: config.detailPath(post.id),
  });

  const canEdit = perms.canUpdate(post);
  const canDelete = perms.canDelete(post);

  // prev/next 가 detail 응답에 실려오면 사용(타입 확장 여지)
  const prev = (post as Post & { prev?: Post }).prev ?? null;
  const next = (post as Post & { next?: Post }).next ?? null;

  return (
    <>
      <SEO {...seo} />
      <main className="overflow-x-hidden bg-white dark:bg-dark-base text-neutral-950 dark:text-neutral-100 antialiased">
        <Section spacing="default">
          <Container>
            <div className="mx-auto max-w-3xl pt-[100px] sm:pt-28">
              {/* 헤더 */}
              <header className="border-b border-black/10 dark:border-white/10 pb-6">
                <button
                  type="button"
                  onClick={() => navigate(config.basePath)}
                  className="mb-4 text-[13px] font-semibold text-neutral-500 dark:text-neutral-400 transition-colors hover:text-neutral-950 dark:hover:text-white"
                >
                  ← 목록으로
                </button>
                <h1 className="text-[clamp(26px,3.5vw,40px)] font-extrabold leading-tight -tracking-[0.03em]">
                  {post.title}
                </h1>
                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-neutral-500 dark:text-neutral-400">
                  {post.authorName && <span>{post.authorName}</span>}
                  <span>{formatBoardDateTime(post.publishedAt)}</span>
                  {post.viewCount !== undefined && (
                    <span>조회 {post.viewCount.toLocaleString('ko-KR')}</span>
                  )}
                  {'startsAt' in post && (
                    <span className="font-mono">
                      {formatBoardDate(post.startsAt)} —{' '}
                      {formatBoardDate(post.endsAt)}
                    </span>
                  )}
                </div>

                {/* 권한 액션 */}
                {(canEdit || canDelete) && (
                  <div className="mt-5 flex gap-2">
                    {canEdit && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(config.editPath(post.id))}
                      >
                        수정
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        variant="danger"
                        size="sm"
                        loading={deleteMutation.isPending}
                        onClick={handleDelete}
                      >
                        삭제
                      </Button>
                    )}
                  </div>
                )}
              </header>

              {/* 본문 */}
              <article className="py-8">
                {post.body ? (
                  <PostViewer content={post.body} />
                ) : (
                  <p className="text-neutral-400 dark:text-neutral-500">본문이 없습니다.</p>
                )}
              </article>

              {/* 첨부 */}
              {post.attachments && post.attachments.length > 0 && (
                <AttachmentList attachments={post.attachments} />
              )}

              {/* 이전/다음 */}
              <DetailNav config={config} prev={prev} next={next} />
            </div>
          </Container>
        </Section>
      </main>
    </>
  );
}
